import { useCallback } from 'react'
import { Utils } from '@Util/utils'
import './styles.scss'

const CLASS_NAME = 'gby-k2g-animation-addons-panel'

export type AnimationAddOnMultiplierSummary = {
  name: string
  value: string
  logoUrl: string | undefined
}

export type MiniGameAddOnSelection = {
  number: number
  selected: boolean
}

export type AnimationAddOnMiniGameSummary = {
  name: string
  logoUrl: string
  jackpotValue: number | null
  selections: MiniGameAddOnSelection[]
}

export type AnimationAddOnPanelProps = {
  display: boolean
  multiplierAddOn: AnimationAddOnMultiplierSummary | undefined
  miniGameAddOn: AnimationAddOnMiniGameSummary | undefined
}

export const AnimationAddOnPanel = (props: AnimationAddOnPanelProps) => {
  const needsDivider = props.multiplierAddOn && props.miniGameAddOn

  const renderMultipliers = useCallback(() => {
    if (!props.multiplierAddOn) return null

    const elements: JSX.Element[] = []

    if (props.multiplierAddOn.logoUrl !== undefined) {
      const logo = (
        <img
          className={`${CLASS_NAME}-multipliers__logo`}
          key={`${CLASS_NAME}-multipliers-logo`}
          src={props.multiplierAddOn.logoUrl}
          alt={props.multiplierAddOn.name}></img>
      )
      elements.push(logo)
    }

    const name = (
      <div className={`${CLASS_NAME}-multipliers__name`} key={`${CLASS_NAME}-multipliers-name`}>
        {props.multiplierAddOn.name}
        {`: `}
      </div>
    )
    const value = (
      <div className={`${CLASS_NAME}-multipliers__value`} key={`${CLASS_NAME}-multipliers-value`}>
        {props.display ? props.multiplierAddOn.value : '--'}
      </div>
    )
    elements.push(name, value)

    return <section className={`${CLASS_NAME}-multipliers`}>{elements}</section>
  }, [props.multiplierAddOn, props.display])

  const renderMiniGames = useCallback(() => {
    if (!props.miniGameAddOn) return null

    const miniGames: JSX.Element[] = []
    const elements: JSX.Element[] = []
    const logo = (
      <img
        className={`${CLASS_NAME}-minigame__logo`}
        key={`${CLASS_NAME}-minigame-logo`}
        src={props.miniGameAddOn.logoUrl}
        alt={props.miniGameAddOn.name}></img>
    )
    elements.push(logo)
    const jackpot = props.miniGameAddOn.jackpotValue ? (
      <div className={`${CLASS_NAME}-minigame__jackpot`} key={`${CLASS_NAME}-minigame-jackpot`}>
        <div
          className={`${CLASS_NAME}-minigame__jackpot-label`}
          key={`${CLASS_NAME}-minigame-jackpot-label`}>
          {`Jackpot: `}
        </div>
        <div
          className={`${CLASS_NAME}-minigame__jackpot-value`}
          key={`${CLASS_NAME}-minigame-jackpot-value`}>
          {props.display
            ? Utils.convertCentsNumberToDollarString(props.miniGameAddOn.jackpotValue)
            : '--'}
        </div>
      </div>
    ) : null

    if (jackpot) {
      elements.push(jackpot)
    }
    const selections = (
      <div
        className={`${CLASS_NAME}-minigame__selections`}
        key={`${CLASS_NAME}-minigame-selections`}>
        {props.miniGameAddOn.selections.map((selection) => {
          return (
            <div
              className={`${CLASS_NAME}-minigame__selection-number`}
              data-state={selection.selected ? 'matched' : 'unmatched'}
              key={`minigame-selection-number-${selection.number}`}>
              {Utils.zeroPadNumber(selection.number)}
            </div>
          )
        })}
      </div>
    )
    elements.push(selections)
    const miniGame = (
      <div key={`minigame-game`} className={`${CLASS_NAME}-minigame__game`}>
        {elements}
      </div>
    )
    miniGames.push(miniGame)

    return (
      <section key={`minigame-container`} className={`${CLASS_NAME}-minigame__container`}>
        {miniGames}
      </section>
    )
  }, [props.miniGameAddOn, props.display])

  if (!props.miniGameAddOn && !props.multiplierAddOn) return null
  return (
    <div className={CLASS_NAME} data-state={props.display ? 'active' : 'dimmed'}>
      {renderMultipliers()}
      {needsDivider ? <hr /> : null}
      {renderMiniGames()}
    </div>
  )
}
